/**
 * Toast Notification System
 *
 * Provides app-wide toast notifications to replace browser alert() calls.
 * Uses a context provider pattern with auto-dismiss timers.
 *
 * Usage:
 *   const { toast } = useToast();
 *   toast.error('Something went wrong');
 *   toast.success('Campaign published');
 *   toast.info('Processing...');
 *   toast.warning('Limit approaching');
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

type ToastVariant = 'info' | 'success' | 'warning' | 'error';

interface ToastItem {
  id: string;
  variant: ToastVariant;
  message: string;
  title?: string;
}

interface ToastContextType {
  toast: {
    info: (message: string, title?: string) => void;
    success: (message: string, title?: string) => void;
    warning: (message: string, title?: string) => void;
    error: (message: string, title?: string) => void;
  };
}

const ToastContext = createContext<ToastContextType | null>(null);

export const useToast = (): ToastContextType => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

const variantStyles: Record<ToastVariant, { bg: string; icon: string; border: string }> = {
  info: {
    bg: 'bg-work-soft',
    icon: 'text-work',
    border: 'border-work/30',
  },
  success: {
    bg: 'bg-ok-soft',
    icon: 'text-ok',
    border: 'border-ok/30',
  },
  warning: {
    bg: 'bg-warn-soft',
    icon: 'text-yellow-500',
    border: 'border-warn/30',
  },
  error: {
    bg: 'bg-bad-soft',
    icon: 'text-bad',
    border: 'border-bad/30',
  },
};

const variantIcons: Record<ToastVariant, React.ReactNode> = {
  info: <Info className="w-5 h-5" />,
  success: <CheckCircle className="w-5 h-5" />,
  warning: <AlertTriangle className="w-5 h-5" />,
  error: <AlertCircle className="w-5 h-5" />,
};

const ToastItem: React.FC<{ item: ToastItem; onDismiss: (id: string) => void }> = ({ item, onDismiss }) => {
  const styles = variantStyles[item.variant];

  return (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg backdrop-blur-sm',
        'animate-in slide-in-from-top-2 fade-in duration-200',
        'max-w-sm w-full',
        styles.bg,
        styles.border
      )}
      role="alert"
    >
      <div className={cn('flex-shrink-0 mt-0.5', styles.icon)}>
        {variantIcons[item.variant]}
      </div>
      <div className="flex-1 min-w-0">
        {item.title && (
          <p className="font-semibold text-sm text-ink mb-0.5">{item.title}</p>
        )}
        <p className="text-sm text-ink-2">{item.message}</p>
      </div>
      <button
        onClick={() => onDismiss(item.id)}
        className="flex-shrink-0 text-ink-3 hover:text-ink-2 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const counterRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((variant: ToastVariant, message: string, title?: string) => {
    const id = `toast-${++counterRef.current}`;
    setToasts(prev => [...prev, { id, variant, message, title }]);
    setTimeout(() => dismiss(id), variant === 'error' ? 6000 : 4000);
  }, [dismiss]);

  const toast = {
    info: (message: string, title?: string) => addToast('info', message, title),
    success: (message: string, title?: string) => addToast('success', message, title),
    warning: (message: string, title?: string) => addToast('warning', message, title),
    error: (message: string, title?: string) => addToast('error', message, title),
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/* Toast container - top-right */}
      {toasts.length > 0 && (
        <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2">
          {toasts.map(t => (
            <ToastItem key={t.id} item={t} onDismiss={dismiss} />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
};

export default ToastProvider;
